import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import Pill from './Pill'

import {
  selectCustomFields,
  fetchCustomFields
} from '../../../../slices/customFields'
import useStyles from './styles'

export type FilterLogic = 'eq' | 'gt' | 'lt' | 'gte' | 'lte'

export type SimpleValue = {
  field_id: string
  value: string
}

export type AdvancedValue = SimpleValue & {
  logic: FilterLogic
}

type FieldPillsProps =
  | {
      advanced?: false
      values: SimpleValue[]
      setValues: React.Dispatch<React.SetStateAction<SimpleValue[]>>
    }
  | {
      advanced: true
      values: AdvancedValue[]
      setValues: React.Dispatch<React.SetStateAction<AdvancedValue[]>>
    }

const FieldPills: React.FC<FieldPillsProps> = ({
  values,
  setValues,
  advanced = false
}) => {
  const dispatch = useAppDispatch()

  const { customFields, loaded, loading } = useAppSelector(selectCustomFields)

  const selectedFields = React.useMemo(() => {
    const selected: LocalCustomField[] = []
    values.forEach(value => {
      const field = customFields.find(field => field.id === value.field_id)
      selected.push(field)
    })
    return selected
  }, [customFields, values])

  const classes = useStyles()

  // Load fields
  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchCustomFields())
    }
  }, [loaded, loading, dispatch])

  const handleDelete = React.useCallback(
    (index: number) => {
      // Remove field from list and values
      setValues(old => {
        const copy = [...old]
        copy.splice(index, 1)
        return copy
      })
    },
    [setValues]
  )

  const handleSet = React.useCallback(
    (index: number, value: string) => {
      // Update value
      setValues(old => {
        const copy = [...old]
        copy[index].value = value
        return copy
      })
    },
    [setValues]
  )

  const handleLogic = React.useCallback(
    (index: number, logic: FilterLogic) => {
      if (!advanced) {
        return
      }

      // Update logic
      setValues(old => {
        const copy = [...old]
        copy[index].logic = logic
        return copy
      })
    },
    [advanced, setValues]
  )

  return (
    <div className={classes.root}>
      {selectedFields.map((field, i) => {
        if (!field) {
          return null
        }

        let value = ''
        let logic: FilterLogic = 'eq'
        if (!advanced) {
          const simpleValue = values[i] as SimpleValue
          value = simpleValue?.value ?? ''
        } else {
          const advancedValue = values[i] as AdvancedValue
          value = advancedValue?.value ?? ''
          logic = advancedValue?.logic ?? 'eq'
        }

        return (
          <Pill
            key={`${field.id}-${i}`}
            advanced={advanced}
            field={field}
            onDelete={() => handleDelete(i)}
            value={value}
            setValue={val => handleSet(i, val)}
            logic={logic}
            setLogic={logic => handleLogic(i, logic)}
          />
        )
      })}
    </div>
  )
}

export default FieldPills
